import { Check } from "lucide-react";
import { inr, SUBCATS, INSTALMENT_LABELS } from "@/lib/calc";

const NOTES = [
  "Discounts other than the Sibling discount are applicable only in the first year of admission.",
  "Books and Uniforms are on actual, and Transport is optional.",
  "Fees once paid is not refundable and Admission Fee is a one-time non-refundable fee.",
  "To avail SPOT (same-day) admission benefits, the parent has to reserve a seat by paying a form fee of Rs.1000/- on the date of enquiry and pay the admission fee within 5 days of the enquiry.",
];

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const Box = ({ on, label, id }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide" data-testid={`paper-cat-${id}`} data-checked={on}>
    <span className={`grid h-3.5 w-3.5 place-items-center border ${on ? "border-plum-900 bg-plum-900 text-white" : "border-slate-400"}`}>
      {on && <Check className="h-2.5 w-2.5" strokeWidth={4} />}
    </span>
    {label}
  </span>
);

const Field = ({ label, value, id }) => (
  <div className="flex items-baseline gap-2 border-b border-dotted border-slate-300 pb-1">
    <span className="shrink-0 text-[10.5px] font-bold uppercase tracking-wider text-slate-700">{label}</span>
    <span className="truncate pb-0.5 text-[12.5px] font-medium leading-normal" data-testid={`paper-${id}`}>{value || "\u00A0"}</span>
  </div>
);

const FeeTable = ({ rows, footLabel, footValue, baseTotal, testid }) => (
  <table className="w-full table-fixed border-collapse text-[11.5px]" data-testid={testid}>
    <colgroup>
      <col style={{ width: "5%" }} /><col style={{ width: "35%" }} /><col style={{ width: "11%" }} />
      <col style={{ width: "24%" }} /><col style={{ width: "12%" }} /><col style={{ width: "13%" }} />
    </colgroup>
    <thead>
      <tr className="bg-plum-900 font-bold text-left text-[10px] font-bold uppercase tracking-wider text-white">
        <th className="px-2 py-1.5">Sr.</th>
        <th className="px-2 py-1.5">Description</th>
        <th className="px-2 py-1.5 text-right">Base ₹</th>
        <th className="px-2 py-1.5">Discount Applied</th>
        <th className="px-2 py-1.5 text-right">Discount ₹</th>
        <th className="px-2 py-1.5 text-right">Final ₹</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.desc} className="border-b border-slate-200">
          <td className="px-2 py-1.5 text-slate-500">{r.sr}</td>
          <td className="px-2 py-1.5">{r.desc}</td>
          <td className="font-num px-2 py-1.5 text-right">{inr(r.base)}</td>
          <td className="px-2 py-1.5 text-slate-600">{r.discLabel}</td>
          <td className="font-num px-2 py-1.5 text-right text-plum-700" data-testid={`${testid}-disc-${r.sr}`}>
            {r.disc === null || r.disc === "" ? "" : r.disc ? `− ${inr(r.disc)}` : "0"}
          </td>
          <td className="font-num px-2 py-1.5 text-right font-semibold" data-testid={`${testid}-final-${r.sr}`}>{inr(r.final)}</td>
        </tr>
      ))}
      <tr className="border-t-2 border-gold-500 bg-gold-50 font-semibold">
        <td className="px-2 py-1.5" colSpan={2}>{baseTotal !== undefined ? "Annual Fee" : ""}</td>
        <td className="font-num px-2 py-1.5 text-right" data-testid={`${testid}-base-total`}>{baseTotal !== undefined ? inr(baseTotal) : ""}</td>
        <td className="whitespace-nowrap px-2 py-1.5 text-right" colSpan={2}>{footLabel}</td>
        <td className="font-num bg-gold-200 px-2 py-1.5 text-right text-plum-950" data-testid={`${testid}-total`}>{inr(footValue)}</td>
      </tr>
    </tbody>
  </table>
);

const PaymentBlock = ({ p }) => (
  <div className="relative mt-4 flex flex-wrap items-center gap-x-8 gap-y-2 border-2 border-gold-500 bg-gold-50 px-4 py-3 text-[11.5px]" data-testid="paper-payment">
    <span className="font-display -rotate-3 border-2 border-gold-600 bg-gold-500 px-3 py-1 text-[13px] font-bold uppercase tracking-[0.2em] text-plum-950">Paid</span>
    <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Payment Mode</div><div className="font-medium" data-testid="paper-payment-mode">{p.mode}</div></div>
    <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Reference No.</div><div className="font-num font-medium" data-testid="paper-payment-ref">{p.reference || "—"}</div></div>
    <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Amount Received ₹</div><div className="font-num text-[13px] font-bold text-gold-800" data-testid="paper-payment-amount">{inr(p.amount)}</div></div>
    <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Payment Date</div><div className="font-medium" data-testid="paper-payment-date">{fmtDate(p.date)}</div></div>
  </div>
);

const FormFeeBlock = ({ f }) => (
  <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-1 border border-plum-900/40 bg-plum-50 px-4 py-2 text-[11px]" data-testid="paper-form-fee">
    <span className="font-display text-[11px] font-bold uppercase tracking-[0.14em] text-plum-900">Form Fee Paid · Seat Reserved</span>
    <div><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">Mode</span><span className="font-medium" data-testid="paper-form-fee-mode">{f.mode}</span></div>
    <div><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">Ref</span><span className="font-num font-medium" data-testid="paper-form-fee-ref">{f.reference || "—"}</span></div>
    <div><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">Amount ₹</span><span className="font-num font-bold text-plum-950" data-testid="paper-form-fee-amount">{inr(f.amount)}</span></div>
    <div><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">Date</span><span className="font-medium" data-testid="paper-form-fee-date">{fmtDate(f.date)}</span></div>
  </div>
);

const PartLine = ({ p, n }) => (
  <div className="flex flex-wrap items-center gap-x-8 gap-y-0.5" data-testid={`paper-partial-row-${n}`}>
    <span className="w-24 text-[10px] font-bold uppercase tracking-wider text-orange-900">Instalment {n}</span>
    <div><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">Mode</span><span className="font-medium">{p.mode}</span></div>
    <div><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">Ref</span><span className="font-num font-medium">{p.reference || "—"}</span></div>
    <div><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">Amount ₹</span><span className="font-num font-bold" data-testid="paper-partial-amount">{inr(p.amount)}</span></div>
    <div><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">Date</span><span className="font-medium">{fmtDate(p.date)}</span></div>
  </div>
);

const PartialBlock = ({ part }) => (
  <div className="mt-2 space-y-1 border border-orange-300 bg-orange-50 px-4 py-2 text-[11px]" data-testid="paper-partial">
    <div className="flex flex-wrap items-center gap-x-6">
      <span className="font-display text-[11px] font-bold uppercase tracking-[0.14em] text-orange-900">{part.parts.length ? "Part-Payments Received" : "Partially Paid"}</span>
      {part.balance != null && (
        <div className="ml-auto"><span className="mr-1.5 text-[10px] font-bold uppercase tracking-wider text-orange-900">Balance Due ₹</span>
          <span className="font-num text-[12px] font-bold text-orange-900" data-testid="paper-partial-balance">{inr(part.balance)}</span>
          {part.by && <span className="ml-1.5 text-orange-900">by {fmtDate(part.by)}</span>}</div>
      )}
    </div>
    {part.parts.map((p, i) => <PartLine key={i} p={p} n={i + 1} />)}
  </div>
);

export const ReceiptPaper = ({ inputs, c, settings, receiptNo, payment, formFee, part, signatureUrl }) => (
  <div id="print-area" className="paper mx-auto px-8 py-7 sm:px-10" data-testid="receipt-paper">
    <div className="flex items-start justify-between gap-6 border-b-2 border-plum-900 pb-4">
      <div>
        <img src="/logo.png" alt={settings?.school_name} className="h-14 w-auto" data-testid="paper-logo" />
        <span className="sr-only" data-testid="paper-school-name">{settings?.school_name}</span>
        {settings?.school_address && <div className="mt-0.5 text-[11px] text-slate-600">{settings.school_address}</div>}
        {settings?.school_contact && <div className="text-[11px] text-slate-600">{settings.school_contact}</div>}
      </div>
      <div className="text-right">
        <div className="font-display text-[13px] font-bold uppercase tracking-[0.14em]">Admission Counselor Receipt</div>
        <div className="text-[11px] text-slate-600">{settings?.academic_year}</div>
        {inputs.grade && <div className="mt-1.5 inline-block bg-plum-700 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white" data-testid="paper-grade">{inputs.grade}</div>}
        {receiptNo && <div className="font-num mt-1 text-[10.5px] text-slate-500" data-testid="paper-receipt-no">No. {receiptNo}</div>}
      </div>
    </div>

    <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5">
      <Field label="Date of Enquiry" value={fmtDate(inputs.enquiryDate)} id="enquiry-date" />
      <Field label="Admission Seeking Grade" value={inputs.grade} id="seeking-grade" />
      <Field label="Child Name" value={inputs.childName} id="child-name" />
      <Field label="Parent Name" value={inputs.parentName} id="parent-name" />
      <Field label="Contact" value={inputs.contact} id="contact" />
      <Field label="Email" value={inputs.email} id="email" />
    </div>

    <div className="mt-4 grid grid-cols-[96px_repeat(5,auto)] items-center justify-start gap-x-5 gap-y-2 border border-slate-200 px-3 py-2.5" data-testid="paper-category-grid">
      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700">Category</span>
      <Box on={inputs.spot} label="Spot" id="spot" />
      <Box on={inputs.revisit} label="Revisit" id="revisit" />
      <span /><span /><span />
      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700">Sub Category</span>
      {SUBCATS.map((s) => <Box key={s.key} on={inputs.sub?.[s.key]} label={s.label} id={s.key} />)}
    </div>
    {inputs.sub?.referral && (
      <div className="mt-2 grid grid-cols-2 gap-x-6" data-testid="paper-referral">
        <Field label="Referral Name" value={inputs.referralName} id="referral-name" />
        <Field label="Referral Mobile No." value={inputs.referralMobile} id="referral-mobile" />
      </div>
    )}
    {inputs.sub?.existingSibling && (
      <div className="mt-2 grid grid-cols-2 gap-x-6" data-testid="paper-sibling">
        <Field label="Sibling Full Name" value={inputs.siblingName} id="sibling-name" />
        <Field label="Sibling Grade" value={inputs.siblingGrade} id="sibling-grade" />
      </div>
    )}

    <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700">Fee and Discount Structure</div>
    <div className="mt-1.5 space-y-3">
      <FeeTable rows={c.rows1} footLabel="Admission & Form Fee ₹" footValue={c.oneTimeTotal} testid="paper-onetime" />
      <FeeTable rows={c.rows2} footLabel="School Fee after discounts ₹" footValue={c.schoolFeeAfterDisc} baseTotal={c.annualFee} testid="paper-annual" />
    </div>

    <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700">Payment Options</div>
    <div className="mt-1.5 grid grid-cols-[1fr_1.5fr_1fr] border border-plum-900 text-[11.5px]">
      <div className="border-r border-plum-900 border-t-[3px] border-t-gold-500 bg-gold-50 p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-gold-800">At the time of Admission ₹</div>
        <div className="font-num mt-2 text-xl font-bold text-plum-950" data-testid="paper-at-admission">{inr(c.atAdmission)}</div>
      </div>
      <div className="border-r border-plum-900 p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Quarter Instalment ₹</div>
        <div className="mt-1.5 space-y-1">
          {c.quarterly.map((q, i) => (
            <div key={i} className="flex justify-between gap-2">
              <span className="text-slate-600">{INSTALMENT_LABELS[i]}</span>
              <span className="font-num font-semibold" data-testid={`paper-q${i + 1}`}>{inr(q)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Monthly Option ₹</div>
        <div className="mt-1.5 flex justify-between text-slate-600"><span>Start Month</span><span className="font-medium text-plum-950">{c.startMonth}</span></div>
        <div className="flex justify-between text-slate-600"><span>End Month</span><span className="font-medium text-plum-950">{c.endMonth}</span></div>
        <div className="font-num mt-1.5 inline-block border-b-2 border-gold-500 text-lg font-semibold" data-testid="paper-monthly">{inr(c.monthly)}</div>
        {c.months !== "" && <div className="text-[10px] text-slate-500">per month × {c.months}</div>}
      </div>
    </div>

    {formFee && <FormFeeBlock f={formFee} />}
    {part && <PartialBlock part={part} />}
    {payment && <PaymentBlock p={payment} />}

    <div className="mt-4 flex items-start justify-between gap-6">
      <ol className="list-decimal space-y-0.5 pl-8 text-[10.5px] text-slate-600">
        <span className="-ml-8 font-bold text-slate-700">PS:</span>
        {NOTES.map((n, i) => <li key={n} className={i === NOTES.length - 1 ? "font-bold text-plum-950" : ""}>{n}</li>)}
      </ol>
      <div className="shrink-0 border border-plum-900/15 px-2 py-1 text-center" data-testid="paper-payment-qr">
        <div className="font-display text-[9.5px] font-bold uppercase leading-tight tracking-[0.12em] text-plum-900">Reserve your seat</div>
        <img src="/payment-qr.png" alt="Scan to pay - Admission AY 2027-28" className="mx-auto mt-1 w-[66px]" data-testid="paper-payment-qr-img" />
      </div>
    </div>

    <div className="mt-4 min-h-[52px] border border-slate-200 p-2 text-[11.5px]">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Remarks (if any): </span>
      <span data-testid="paper-remarks">{inputs.remarks}</span>
    </div>

    <div className="mt-8 grid grid-cols-2 gap-10 text-[11.5px]">
      <div>
        <div className="flex items-baseline gap-2 border-b border-dotted border-slate-300 pb-1">
          <span className="shrink-0 text-[10.5px] font-bold uppercase tracking-wider text-slate-700">Adm Counsellor Name</span>
          <span className="break-words pb-0.5 text-[12.5px] font-medium leading-normal" data-testid="paper-counselor-name">{inputs.counselorName || "\u00A0"}</span>
          {inputs.counselorMobile && <span className="ml-auto shrink-0 whitespace-nowrap pb-0.5 font-num text-[12px] font-semibold text-plum-900" data-testid="paper-counselor-mobile">{inputs.counselorMobile}</span>}
        </div>
        <div className="mt-1 flex h-14 items-end border-b border-slate-400">
          {signatureUrl && (
            <img key={signatureUrl} src={signatureUrl} alt="Counsellor signature" className="max-h-[52px] max-w-[200px] object-contain mix-blend-multiply"
              onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="paper-counselor-signature-img" />
          )}
        </div>
        <div className="mt-1 flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-700">
          <span>Counsellor Signature</span><span className="normal-case tracking-normal text-plum-900" data-testid="paper-counselor-signature">{inputs.counselorName}</span>
        </div>
      </div>
      <div>
        <Field label="Parent Name" value={inputs.parentName} id="parent-sign-name" />
        <div className="mt-1 h-14 border-b border-slate-400" />
        <div className="mt-1 flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-700">
          <span>Parent Signature</span><span className="normal-case tracking-normal text-plum-900" data-testid="paper-parent-signature">{inputs.parentName}</span>
        </div>
      </div>
    </div>
  </div>
);
